<!-- FinancialProjection.vue - Enhanced with Tax Reference Integration -->
<template>
  <div class="financial-projection">
    <h2>Financial Planning</h2>

    <!-- Inflation Notice -->
    <div v-if="dollarType === 'today'" class="inflation-notice">
      <span>Values shown in today's dollars ({{ (inflationRate * 100).toFixed(1) }}% inflation adjustment)</span>
    </div>

    <!-- Charts Section -->
    <div class="charts-section">
      <div class="chart-tabs">
        <button 
          @click="activeChart = 'cashflow'"
          class="chart-tab"
          :class="{ active: activeChart === 'cashflow' }"
        >
          💰 Cash Flow
        </button>
        <button 
          @click="activeChart = 'sankey'"
          class="chart-tab"
          :class="{ active: activeChart === 'sankey' }"
        >
          🌊 Flow Diagram
        </button>
        <button 
          @click="activeChart = 'networth'"
          class="chart-tab"
          :class="{ active: activeChart === 'networth' }"
        >
          📈 Net Worth
        </button>
        <button 
          @click="activeChart = 'portfolio'"
          class="chart-tab"
          :class="{ active: activeChart === 'portfolio' }"
        >
          📊 Portfolio Value
        </button>
        <button 
          @click="activeChart = 'montecarlo'"
          class="chart-tab"
          :class="{ active: activeChart === 'montecarlo' }"
        >
          📊 Monte Carlo
        </button>
        <!-- NEW: Roth Conversion Tab -->
        <!-- <button 
          @click="activeChart = 'roth'"
          class="chart-tab"
          :class="{ active: activeChart === 'roth' }"
        >
          🔄 Roth Conversion
        </button> -->
      </div>

      <div class="chart-content">
        <CashFlowChart 
          v-if="activeChart === 'cashflow'"
          :year-rows="adjustedYearRows"
          :items="cashFlowItems"
          :projection="cashFlowProjectionForChart"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
        />
        <SankeyChart 
          v-if="activeChart === 'sankey'"
          :items="cashFlowItems"
          :projection="cashFlowProjectionForChart"
          :year-rows="adjustedYearRows"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
        />
        <NetWorthChart 
          v-if="activeChart === 'networth'"
          :year-rows="adjustedYearRows"
          :items="netWorthItems"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
        />
        <PortfolioValueChart 
          v-if="activeChart === 'portfolio'"
          :year-rows="adjustedYearRows"
          :items="netWorthItems"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
        />
        <MonteCarloChart 
          v-if="activeChart === 'montecarlo'"
          :year-rows="adjustedYearRows"
          :items="netWorthItems"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
        />
        <!-- NEW: Roth Conversion Chart -->
        <RothConversionChart 
          v-if="activeChart === 'roth'"
          :year-rows="adjustedYearRows"
          :items="cashFlowItems"
          :format-currency="formatCurrency"
          :dollar-type="dollarType"
          :personal-info="personalInfo"
          :assumptions="assumptions"
        />
      </div>
    </div>

    <!-- Collapsible Items Management Section -->
    <div class="items-section">
      <div class="items-section-header" @click="toggleItemsSection">
        <h3>Inputs and Assumptions</h3>
        <div class="items-summary">
          <button class="collapse-toggle" :class="{ expanded: itemsSectionExpanded }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="itemsSectionExpanded" class="items-content">
        <!-- Personal Information & Assumptions Section -->
        <div class="personal-info-section">
          <div class="personal-assumptions-grid">
            <!-- Personal Information Side -->
            <div class="personal-info-side">
              <h4>Personal Information</h4>
              <div class="personal-cards">
                <PersonalInfoCard
                  :person-info="personalInfo.primary"
                  :person-name="getPrimaryName()"
                  person-type="primary"
                  @update-personal-info="updatePersonalInfo"
                />
                <PersonalInfoCard
                  v-if="hasSpouseInfo"
                  :person-info="personalInfo.spouse"
                  :person-name="getSpouseName()"
                  person-type="spouse"
                  @update-personal-info="updatePersonalInfo"
                />
                <div v-else class="add-spouse-card">
                  <button @click="addSpouseInfo" class="btn-add-spouse">
                    + Add Spouse
                  </button>
                </div>
              </div>
            </div>

            <!-- Assumptions Side -->
            <div class="assumptions-side">
              <h4>Planning Assumptions</h4>
              <AssumptionsCard
                :assumptions="assumptions"
                @update-assumptions="updateAssumptions"
              />
            </div>
          </div>
        </div>

        <!-- Financial Items Section -->
        <div class="financial-items-section">
          <div class="four-column-layout">
            <!-- Income Column -->
            <CompactFinancialSection 
              title="Income" 
              type="income"
              item-category="cashflow"
              :items="incomeItems"
              @add-item="addIncomeItem"
              @remove-item="removeCashFlowItem"
              @update-item="updateCashFlowItem"
            />

            <!-- Expenses Column -->
            <CompactFinancialSection 
              title="Expenses" 
              type="expense"
              item-category="cashflow"
              :items="expenseItems"
              @add-item="addExpenseItem"
              @remove-item="removeCashFlowItem"
              @update-item="updateCashFlowItem"
            />

            <!-- Assets Column -->
            <CompactFinancialSection 
              title="Assets" 
              type="asset"
              item-category="networth"
              :items="assetItems"
              :items-by-category="assetsByCategory"
              @add-item="addAssetItem"
              @remove-item="removeNetWorthItem"
              @update-item="updateNetWorthItem"
            />

            <!-- Liabilities Column -->
            <CompactFinancialSection 
              title="Liabilities" 
              type="liability"
              item-category="networth"
              :items="liabilityItems"
              @add-item="addLiabilityItem"
              @remove-item="removeNetWorthItem"
              @update-item="updateNetWorthItem"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Unified Financial Table -->
    <div class="items-section">
      <div class="items-section-header" @click="toggleTableSection">
        <h3>Table of Projections</h3>
        <div class="items-summary">
          <button class="collapse-toggle" :class="{ expanded: tableSectionExpanded }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="tableSectionExpanded" class="table-content">
        <UnifiedFinancialTable 
          :year-rows="adjustedYearRows"
          :cash-flow-items="cashFlowItems"
          :net-worth-items="netWorthItems"
          :personal-info="personalInfo"
          :format-currency="formatCurrency"
          :expanded-sections="expandedSections"
          :dollar-type="dollarType"
          @toggle-section="toggleSection"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import CompactFinancialSection from './CompactFinancialSection.vue';
import PersonalInfoCard from './PersonalInfoCard.vue';
import AssumptionsCard from './AssumptionsCard.vue';
import CashFlowChart from './charts/CashFlowChart.vue';
import SankeyChart from './charts/SankeyChart.vue';
import NetWorthChart from './charts/NetWorthChart.vue';
import UnifiedFinancialTable from './UnifiedFinancialTable.vue';
import PortfolioValueChart from './charts/PortfolioValueChart.vue';
import MonteCarloChart from './charts/MonteCarloChart.vue';
import RothConversionChart from './charts/RothConversionChart.vue';

// Import tax reference system
import { DEFAULT_ECONOMIC_ASSUMPTIONS } from './planning_assumptions/StateTaxReference2025.js';

export default {
  name: 'FinancialProjection',
  components: {
    CompactFinancialSection,
    PersonalInfoCard,
    AssumptionsCard,
    CashFlowChart,
    SankeyChart,
    NetWorthChart,
    UnifiedFinancialTable,
    PortfolioValueChart,
    MonteCarloChart,
    RothConversionChart
  },
  props: {
    cashFlowItems: {
      type: Array,
      default: () => []
    },
    netWorthItems: {
      type: Array,
      default: () => []
    },
    personalInfo: {
      type: Object,
      default: () => ({})
    },
    dollarType: {
      type: String,
      default: 'today' // 'today' or 'future'
    },
    inflationRate: {
      type: Number,
      default: 0.025
    }
  },
  emits: ['update-cash-flow-items', 'update-net-worth-items', 'update-inflation-rate', 'update-personal-info'],
  setup(props, { emit }) {
    const currentYear = new Date().getFullYear();
    const horizon = 30;
    const activeChart = ref('cashflow');
    const expandedSections = ref(new Set());
    const itemsSectionExpanded = ref(false);
    const tableSectionExpanded = ref(false);

    // Enhanced assumptions - now using tax reference system defaults
    const assumptions = ref({
      ...DEFAULT_ECONOMIC_ASSUMPTIONS,
      inflationRate: props.inflationRate // Override with prop value
    });

    // Watch for inflation rate changes from parent
    watch(() => props.inflationRate, (newRate) => {
      assumptions.value.inflationRate = newRate;
    });

    // Calculate inflation discount factor for a given year
    const getInflationDiscount = (year) => {
      if (props.dollarType === 'future') return 1;
      const yearsFromNow = year - currentYear;
      return Math.pow(1 / (1 + props.inflationRate), yearsFromNow);
    };

    // Generate years
    const years = computed(() => {
      const allItems = [...props.cashFlowItems, ...props.netWorthItems];
      const maxEnd = allItems.reduce((max, item) => {
        const end = item.end_year ?? (currentYear + horizon);
        return Math.max(max, end);
      }, currentYear + horizon);

      const yrs = [];
      for (let y = currentYear; y <= maxEnd; y++) yrs.push(y);
      return yrs;
    });

    // Total items count for summary
    const totalItemsCount = computed(() => {
      return props.cashFlowItems.length + props.netWorthItems.length;
    });

    // Personal info computed properties
    const hasSpouseInfo = computed(() => {
      return props.personalInfo?.spouse && (
        props.personalInfo.spouse.dateOfBirth ||
        props.personalInfo.spouse.targetRetirementAge ||
        props.personalInfo.spouse.lifeExpectancy ||
        props.personalInfo.spouse.legalFirstName
      );
    });

    const getPrimaryName = () => {
      return props.personalInfo?.primary?.legalFirstName || 'Primary';
    };

    const getSpouseName = () => {
      return props.personalInfo?.spouse?.legalFirstName || 'Spouse';
    };

    // Separate cash flow items with their original indices
    const incomeItems = computed(() => {
      const result = [];
      props.cashFlowItems.forEach((item, index) => {
        if (item.type === 'income') {
          result.push({ ...item, originalIndex: index });
        }
      });
      return result;
    });

    const expenseItems = computed(() => {
      const result = [];
      props.cashFlowItems.forEach((item, index) => {
        if (item.type === 'expense') {
          result.push({ ...item, originalIndex: index });
        }
      });
      return result;
    });

    // Separate net worth items with their original indices
    const assetItems = computed(() => {
      const result = [];
      props.netWorthItems.forEach((item, index) => {
        if (item.type === 'asset') {
          result.push({ ...item, originalIndex: index });
        }
      });
      return result;
    });

    const liabilityItems = computed(() => {
      const result = [];
      props.netWorthItems.forEach((item, index) => {
        if (item.type === 'liability') {
          result.push({ ...item, originalIndex: index });
        }
      });
      return result;
    });

    // Group assets by category
    const assetsByCategory = computed(() => {
      const grouped = {};
      assetItems.value.forEach(item => {
        const category = item.category || 'Other Assets';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push({ ...item });
      });
      return grouped;
    });

    // Calculate cash flow projections (nominal values)
    const cashFlowProjections = computed(() => {
      const projections = {};
      
      props.cashFlowItems.forEach((item, index) => {
        const itemKey = item.id;
        projections[itemKey] = {};
        
        years.value.forEach(year => {
          let value = 0;
          
          if (year >= (item.start_year || currentYear) && 
              (item.end_year == null || year <= item.end_year)) {
            
            let base = 0;
            const amount = parseFloat(item.amount) || 0;
            
            if (item.frequency === 'One-time') {
              base = year === (item.start_year || currentYear) ? amount : 0;
            } else if (item.frequency === 'Annually') {
              base = amount;
            } else if (item.frequency === 'Monthly') {
              base = amount * 12;
            }
            
            const periods = year - (item.start_year || currentYear);
            const growthRate = parseFloat(item.growth_rate) || 0;
            value = base * Math.pow(1 + growthRate, periods);
            
            if (item.type === 'expense') {
              value = -value;
            }
          }
          
          projections[itemKey][year] = value;
        });
      });
      
      return projections;
    });

    // Helper: treat only investment/cash assets as liquid
    const isLiquid = (a) =>
      a.type === 'asset' &&
      (a.category === 'Investments' || a.category === 'Cash & Bank');

    const wealthEngine = computed(() => {
      const result = {};
      const balances = {}; // track asset & liability balances as positive values

      const lastYear = years.value[years.value.length - 1];

      // Liquid items active in a given year
      const eligibleLiquidFor = (year) =>
        props.netWorthItems
          .filter(isLiquid)
          .filter(a =>
            year >= (a.start_year || currentYear) &&
            year <= (a.end_year ?? lastYear)
          )
          .map(a => a.id);

      years.value.forEach(year => {
        // 1) Initialize items that begin this year
        props.netWorthItems.forEach(item => {
          const start = item.start_year || currentYear;
          const end   = item.end_year ?? lastYear;
          if (year === start && year <= end) {
            const amt = Number(item.amount) || 0;
            if (item.type === 'asset')      balances[item.id] = amt;
            else if (item.type === 'liability') balances[item.id] = Math.max(0, amt);
          }
        });

        // 2) Compute net savings from cash-flow (nominal)
        let totalIncome = 0, totalExpenses = 0;
        props.cashFlowItems.forEach(item => {
          const v = (cashFlowProjections.value[item.id]?.[year]) || 0;
          if (item.type === 'income') totalIncome += v;
          else                        totalExpenses += Math.abs(v);
        });
        const netSavings = totalIncome - totalExpenses;

        // 3) Grow balances & apply contributions/payments
        props.netWorthItems.forEach(item => {
          const start = item.start_year || currentYear;
          const end   = item.end_year ?? lastYear;
          if (year >= start && year <= end && balances[item.id] != null) {
            const rate    = Number(item.growth_rate) || 0;
            const contrib = Number(item.contribution_rate) || 0;
            balances[item.id] *= (1 + rate);
            balances[item.id] += contrib;
            if (item.type === 'liability') {
              balances[item.id] = Math.max(0, balances[item.id]);
            }
          }
        });

        // 4) Route deposits/withdrawals
        const eligibleLiquid = eligibleLiquidFor(year);
        const activeLiquid   = eligibleLiquid.filter(id => (balances[id] ?? 0) > 0);
        let portfolioValue   = activeLiquid.reduce((s, id) => s + balances[id], 0);

        let withdrawals = 0, shortfall = 0;

        if (netSavings > 0) {
          const denom = portfolioValue;
          if (denom > 0) {
            activeLiquid.forEach(id => {
              const share = balances[id] / denom;
              balances[id] += netSavings * share;
            });
          } else if (eligibleLiquid.length) {
            // dump into first eligible liquid account (e.g., cash)
            const k = eligibleLiquid[0];
            balances[k] = (balances[k] || 0) + netSavings;
          }
          portfolioValue =
            eligibleLiquid.reduce((tot, id) => tot + (balances[id] || 0), 0);
        } else if (netSavings < 0) {
          const need = Math.abs(netSavings);
          withdrawals = Math.min(need, portfolioValue);
          shortfall   = Math.max(0, need - portfolioValue);
          if (withdrawals > 0 && portfolioValue > 0) {
            const ratio = withdrawals / portfolioValue;
            activeLiquid.forEach(id => {
              balances[id] *= (1 - ratio);
              balances[id] = Math.max(0, balances[id]);
            });
            portfolioValue -= withdrawals;
          }
        }

        // 5) Totals (only count items active this year)
        let totalAssets = 0, totalLiabilities = 0;
        props.netWorthItems.forEach(item => {
          const start = item.start_year || currentYear;
          const end   = item.end_year ?? lastYear;
          if (year >= start && year <= end) {
            const v = balances[item.id] ?? 0;
            if (item.type === 'asset')      totalAssets += Math.max(0, v);
            else                            totalLiabilities += Math.max(0, v);
          }
        });

        // Build a snapshot (zero when out-of-range) for table charts
        const byId = Object.fromEntries(
          props.netWorthItems.map(item => {
            const start = item.start_year || currentYear;
            const end   = item.end_year ?? lastYear;
            const inRange = year >= start && year <= end;
            return [item.id, inRange ? (balances[item.id] ?? 0) : 0];
          })
        );

        result[year] = {
          byId,
          portfolioValue,
          withdrawals,
          shortfall,
          netSavings,
          totalAssets,
          totalLiabilities
        };
      });

      return result;
    });

    // Create unified year rows with nominal values
    const yearRows = computed(() => {
      return years.value.map(year => {
        const row = { year };

        // Cash-flow item columns (unchanged)
        props.cashFlowItems.forEach(item => {
          row[`cf_${item.id}`] = cashFlowProjections.value[item.id]?.[year] || 0;
        });

        // Per-item balances from unified engine
        const snap = wealthEngine.value[year] || { byId: {} };

        props.netWorthItems.forEach(item => {
          const start = item.start_year || currentYear;
          const end   = item.end_year ?? (currentYear + horizon);
          const inRange = year >= start && year <= end;
          const bal = inRange ? (snap.byId?.[item.id] ?? 0) : 0;
          row[`nw_${item.id}`] = item.type === 'liability' ? -bal : bal;
        });

        // Summary from unified engine
        row.totalAssets     = snap.totalAssets || 0;
        row.totalLiabilities= snap.totalLiabilities || 0;
        row.netWorth        = (snap.totalAssets || 0) - (snap.totalLiabilities || 0);

        // Cash-flow summaries (same as before so the numbers match your columns)
        let totalIncome = 0, totalExpenses = 0;
        props.cashFlowItems.forEach(item => {
          const v = cashFlowProjections.value[item.id]?.[year] || 0;
          if (item.type === 'income') totalIncome += v; else totalExpenses += Math.abs(v);
        });
        row.totalIncome = totalIncome;
        row.totalExpenses = totalExpenses;
        row.savings = totalIncome - totalExpenses;

        // Portfolio columns now come from the same engine
        row.portfolioValue = snap.portfolioValue || 0;
        row.withdrawals    = snap.withdrawals || 0;
        row.shortfall      = snap.shortfall || 0;

        return row;
      });
    });

    // Adjusted year rows with inflation discount
    const adjustedYearRows = computed(() => {
      return yearRows.value.map(row => {
        const discount = getInflationDiscount(row.year);
        const adjustedRow = { ...row };
        
        // Apply inflation adjustment to all monetary values
        Object.keys(adjustedRow).forEach(key => {
          if (key !== 'year' && typeof adjustedRow[key] === 'number') {
            adjustedRow[key] = adjustedRow[key] * discount;
          }
        });
        
        return adjustedRow;
      });
    });

    // Calculate cash flow projections for charts (with inflation adjustment)
    const cashFlowProjectionForChart = computed(() => {
      return props.cashFlowItems.map((item, index) => {
        const flows = {};
        const itemKey = item.id;
        
        years.value.forEach(year => {
          const nominalValue = cashFlowProjections.value[itemKey]?.[year] || 0;
          const discount = getInflationDiscount(year);
          flows[year] = nominalValue * discount;
        });
        
        const total = Object.values(flows).reduce((sum, x) => sum + (parseFloat(x) || 0), 0);
        return { 
          id: item.id || `item_${index}`, 
          name: item.name || 'Unnamed Item', 
          flows, 
          total 
        };
      });
    });

    // Item management functions
    function addIncomeItem() {
      const newItems = [...props.cashFlowItems, {
        id: `income_${Date.now()}_${Math.random()}`,
        name: '',
        amount: 0,
        frequency: 'Annually',
        start_year: currentYear,
        end_year: currentYear + horizon,
        growth_rate: assumptions.value.defaultIncomeGrowth,
        type: 'income'
      }];
      emit('update-cash-flow-items', newItems);
    }

    function addExpenseItem() {
      const newItems = [...props.cashFlowItems, {
        id: `expense_${Date.now()}_${Math.random()}`,
        name: '',
        amount: 0,
        frequency: 'Annually',
        start_year: currentYear,
        end_year: currentYear + horizon,
        growth_rate: assumptions.value.defaultExpenseGrowth,
        type: 'expense'
      }];
      emit('update-cash-flow-items', newItems);
    }

    function addAssetItem(category = null) {
      const defaultGrowthRates = {
        'Cash & Bank': assumptions.value.defaultCashGrowth,
        'Investments': assumptions.value.defaultAssetGrowth,
        'Real Estate': assumptions.value.defaultRealEstateGrowth,
        'Other Assets': assumptions.value.defaultAssetGrowth
      };
      
      const newItems = [...props.netWorthItems, {
        id: `asset_${Date.now()}_${Math.random()}`,
        name: '',
        amount: 0,
        growth_rate: defaultGrowthRates[category] || assumptions.value.defaultAssetGrowth,
        contribution_rate: 0,
        start_year: currentYear,
        end_year: currentYear + horizon,
        type: 'asset',
        category: category || 'Other Assets'
      }];
      emit('update-net-worth-items', newItems);
    }

    function addLiabilityItem() {
      const newItems = [...props.netWorthItems, {
        id: `liability_${Date.now()}_${Math.random()}`,
        name: '',
        amount: 0,
        growth_rate: assumptions.value.defaultLiabilityRate,
        contribution_rate: 0,
        start_year: currentYear,
        end_year: currentYear + horizon,
        type: 'liability'
      }];
      emit('update-net-worth-items', newItems);
    }

    function removeCashFlowItem(index) {
      const newItems = [...props.cashFlowItems];
      newItems.splice(index, 1);
      emit('update-cash-flow-items', newItems);
    }

    function updateCashFlowItem(index, field, value) {
      const newItems = [...props.cashFlowItems];
      if (newItems[index]) {
        newItems[index][field] = value;
        emit('update-cash-flow-items', newItems);
      }
    }

    function removeNetWorthItem(index) {
      const newItems = [...props.netWorthItems];
      newItems.splice(index, 1);
      emit('update-net-worth-items', newItems);
    }

    function updateNetWorthItem(index, field, value) {
      const newItems = [...props.netWorthItems];
      if (newItems[index]) {
        newItems[index][field] = value;
        emit('update-net-worth-items', newItems);
      }
    }

    function toggleSection(section) {
      if (expandedSections.value.has(section)) {
        expandedSections.value.delete(section);
      } else {
        expandedSections.value.add(section);
      }
    }

    function toggleItemsSection() {
      itemsSectionExpanded.value = !itemsSectionExpanded.value;
    }

    function toggleTableSection() {
      tableSectionExpanded.value = !tableSectionExpanded.value;
    }

    function addSpouseInfo() {
      // Create a new personalInfo object with spouse data initialized
      const updatedPersonalInfo = {
        ...props.personalInfo,
        spouse: {
          legalFirstName: '',
          dateOfBirth: null,
          targetRetirementAge: 65,
          lifeExpectancy: 85
        }
      };
      
      // Emit an event to update the personal info in the parent component
      // You'll need to add this emit to your emits array: 'update-personal-info'
      emit('update-personal-info', updatedPersonalInfo);
    }

    // Also update the updatePersonalInfo function to properly handle spouse updates:
    function updatePersonalInfo(personType, field, value) {
      let updatedPersonalInfo = { ...props.personalInfo };
      
      // Initialize the person object if it doesn't exist
      if (!updatedPersonalInfo[personType]) {
        updatedPersonalInfo[personType] = {};
      }
      
      if (field === 'remove' && personType === 'spouse') {
        // Remove spouse entirely
        delete updatedPersonalInfo.spouse;
      } else {
        // Update the specific field
        updatedPersonalInfo[personType][field] = value;
      }
      
      // Emit the updated personal info
      emit('update-personal-info', updatedPersonalInfo);
      
      console.log('Updated personal info:', personType, field, value);
    }

    function updateAssumptions(field, value) {
      assumptions.value[field] = value;
      
      // Special handling for inflation rate - also update parent
      if (field === 'inflationRate') {
        emit('update-inflation-rate', value);
      }
      
      console.log('Updated assumption:', field, value);
    }

    function formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(val);
    }

    return {
      activeChart,
      expandedSections,
      itemsSectionExpanded,
      tableSectionExpanded,
      totalItemsCount,
      hasSpouseInfo,
      addSpouseInfo, 
      assumptions,
      incomeItems,
      expenseItems,
      assetItems,
      liabilityItems,
      assetsByCategory,
      yearRows,
      adjustedYearRows,
      cashFlowProjectionForChart,
      getPrimaryName,
      getSpouseName,
      addIncomeItem,
      addExpenseItem,
      addAssetItem,
      addLiabilityItem,
      removeCashFlowItem,
      updateCashFlowItem,
      removeNetWorthItem,
      updateNetWorthItem,
      toggleSection,
      toggleItemsSection,
      toggleTableSection,
      updatePersonalInfo,
      updateAssumptions,
      formatCurrency
    };
  }
};
</script>
